import React, { useEffect, useState } from 'react';

import { Button, notification, FormItemProps, Modal, Radio, Space, Input, Form, Tag, Tooltip, Typography } from 'antd';

import { PaperClipOutlined } from '@ant-design/icons';
import type { AxiosError } from 'axios';
import { get } from '@partsbadger/utils';

import { ErrorRender, FormErrorParser } from '@partsbadger/library';

interface IProps {
    lineItemId: number | undefined;
    show3DApproval: boolean;
    onClose: () => void;
}

import { observer } from 'mobx-react';

import SalesOrderStore from '../../../stores/SalesOrderStore';
import { getLineItemLabel, isPending3DApproval } from './helpers';
import { PartPreview } from '@/components/Quotes';
import { ThreeDViewerModal } from '@/components/ThreeDViewerModal';

const ThreeDApproval: React.FC<IProps> = ({ lineItemId, show3DApproval, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [urlFile3D, setUrlFile3D] = useState<string | null>(null);
    const [signedDownloadUrl, setSignedDownloadUrl] = useState<string | null>(null);
    const [form] = Form.useForm();

    const { order_items, order } = SalesOrderStore;

    const formItemLayout: FormItemProps = {
        labelCol: { span: 24 },
        wrapperCol: { span: 14 },
        labelAlign: 'left',
    };

    const handleSubmit = () => {
        setLoading(true);
        form.validateFields()
            .then(async values => {
                const payload = {
                    id: lineItemId,
                    production_3d_file_approval_status: values.production_3d_file_approval_status,
                    production_3d_file_approval_feedback: values?.production_3d_file_approval_feedback,
                };

                if (values.production_3d_file_approval_status === 'Need 3D Approval') {
                    notification.error({
                        message:
                            'Please ensure you have selected either "Approve" or "Reject" before clicking on the "Submit" button.',
                        placement: 'topRight',
                    });
                    setLoading(false);
                    return true;
                }

                if (
                    values.production_3d_file_approval_status === 'Rejected' &&
                    (values?.production_3d_file_approval_feedback?.length < 5 ||
                        values?.production_3d_file_approval_feedback?.length > 200)
                ) {
                    notification.error({
                        message:
                            'Please note that the minimum character limit is 5, while the maximum allowed is 200 characters.',
                        placement: 'topRight',
                    });
                    setLoading(false);
                    return true;
                }

                if (order && lineItemId) {
                    await SalesOrderStore.threeDApproval(order.id, payload)
                        .then(() => {
                            form.resetFields();
                        })
                        .catch(error => {
                            setLoading(false);

                            notification.error({
                                message: 'Error',
                                description: <ErrorRender error={error} />,
                            });
                        });
                }

                setLoading(false);
            })
            .catch(errorInfo => {
                setLoading(false);

                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Please input all required fields',
                    description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                    placement: 'top',
                    maxCount: 1,
                });
            });
    };

    const getLineItem = order_items?.find(item => item.id === lineItemId);
    const lineItemLabel = getLineItemLabel(getLineItem);

    const getPreviewUrl = async () => {
        if (!getLineItem?.production_3d_file || !lineItemId) {
            return null;
        }

        try {
            const response = await get(
                `/customer/sales-order-line-items/${lineItemId}/production-3d-file-threed-signed-url/`
            );

            if (typeof response === 'string') {
                return response;
            }

            if (response && typeof response === 'object' && 'url' in response && typeof response.url === 'string') {
                return response.url;
            }

            throw new Error('Invalid signed URL response');
        } catch (error) {
            notification.error({
                message: 'Error',
                description: <ErrorRender error={error as AxiosError | undefined} />,
            });

            return null;
        }
    };

    const getDownloadUrl = async () => {
        if (!getLineItem?.production_3d_file || !lineItemId) {
            return null;
        }

        try {
            const response = await get(`/customer/sales-order-line-items/${lineItemId}/production-3d-file-signed-url/`);

            if (typeof response === 'string') {
                return response;
            }

            if (response && typeof response === 'object' && 'url' in response && typeof response.url === 'string') {
                return response.url;
            }

            throw new Error('Invalid signed URL response');
        } catch (error) {
            notification.error({
                message: 'Error',
                description: <ErrorRender error={error as AxiosError | undefined} />,
            });

            return null;
        }
    };

    const preparePreviewUrl = async () => {
        const signedUrl = await getPreviewUrl();

        if (signedUrl) {
            setUrlFile3D(signedUrl);
        }
    };

    const prepareDownloadUrl = async () => {
        const signedUrl = await getDownloadUrl();

        if (signedUrl) {
            setSignedDownloadUrl(signedUrl);
        }
    };

    const handleDownload = async () => {
        const getLineItemData = order_items.find(item => item.id === lineItemId);

        if (getLineItemData?.production_3d_file) {
            const signedUrl = await getDownloadUrl();

            if (signedUrl) {
                window.open(signedUrl, '_blank');
            }
        }
    };

    useEffect(() => {
        setUrlFile3D(null);
        setSignedDownloadUrl(null);

        if (show3DApproval && getLineItem?.production_3d_file?.id) {
            preparePreviewUrl();
            prepareDownloadUrl();
        }
    }, [show3DApproval, getLineItem?.production_3d_file?.id]);

    const switchStatus = (status: string) => {
        switch (status) {
            case 'Approved':
                return (
                    <Tag style={{ marginLeft: 10 }} color="green">
                        Approved
                    </Tag>
                );
            case 'Rejected':
                return (
                    <Tag style={{ marginLeft: 10 }} color="orange">
                        On Revision
                    </Tag>
                );
            default:
                return (
                    <Radio.Group style={{ marginLeft: 10 }}>
                        <Space direction="horizontal">
                            <Radio value="Approved">Approve</Radio>
                            <Radio value="Rejected">Reject</Radio>
                        </Space>
                    </Radio.Group>
                );
        }
    };

    return (
        <Modal
            open={show3DApproval}
            footer={[
                <>
                    {isPending3DApproval(getLineItem?.production_3d_file_approval_status) ? (
                        <div key={1} className="flex justify-between">
                            <Button
                                onClick={() => {
                                    onClose();
                                }}
                                size="large"
                                loading={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                loading={loading}
                                onClick={() => {
                                    handleSubmit();
                                }}
                            >
                                Submit
                            </Button>
                        </div>
                    ) : (
                        <div className="flex justify-end">
                            <Button
                                onClick={() => {
                                    onClose();
                                }}
                                size="large"
                                loading={loading}
                                type="primary"
                                className="grid justify-items-end"
                            >
                                Close
                            </Button>
                        </div>
                    )}
                </>,
            ]}
            centered={true}
            width={600}
            onCancel={() => onClose()}
            destroyOnClose={true}
            title={lineItemLabel ? `3D Approval - ${lineItemLabel}` : '3D Approval'}
        >
            <Form form={form}>
                <div className="flex flex-col">
                    <p>
                        Based on the information provided, it appears that our engineering team has completed the 3D
                        file. Before proceeding with further steps, it is recommended to verify if the file is suitable
                        for approval.
                    </p>

                    <div className="flex justify-between">
                        <Form.Item label="3D File:" {...formItemLayout} className="w-full">
                            <div className="flex flex-row items-center gap-3">
                                <ThreeDViewerModal url={urlFile3D}>
                                    <div className="rounded-lg overflow-hidden border-2 border-slate-300 bg-slate-100 cursor-pointer">
                                        <PartPreview
                                            thumbnail={
                                                getLineItem?.production_3d_file?.image ?? getLineItem?.thumbnail ?? null
                                            }
                                            has_file_3d={!!getLineItem?.production_3d_file}
                                            width="85px"
                                            maxHeight="48px"
                                        />
                                    </div>
                                </ThreeDViewerModal>
                                <div style={{ maxWidth: 260 }}>
                                    <Tooltip title="File 3D">
                                        <Typography.Link
                                            type="secondary"
                                            href={signedDownloadUrl ?? undefined}
                                            download
                                            ellipsis
                                            onClick={async event => {
                                                if (!signedDownloadUrl) {
                                                    event.preventDefault();
                                                    await handleDownload();
                                                }
                                            }}
                                        >
                                            <PaperClipOutlined />
                                            &nbsp;
                                            {getLineItem?.production_3d_file?.filename}
                                        </Typography.Link>
                                    </Tooltip>
                                </div>
                            </div>
                        </Form.Item>

                        <Form.Item
                            label="Do you approve this 3D File?"
                            {...formItemLayout}
                            name="production_3d_file_approval_status"
                            rules={[{ required: true, message: 'Please choose Approve or Reject' }]}
                            initialValue={getLineItem?.production_3d_file_approval_status}
                            className="w-full"
                        >
                            {getLineItem && switchStatus(getLineItem?.production_3d_file_approval_status)}
                        </Form.Item>
                    </div>

                    <Form.Item
                        dependencies={['production_3d_file_approval_status']}
                        hasFeedback
                        noStyle
                        className="w-full"
                    >
                        {({ getFieldValue }) => {
                            return getFieldValue('production_3d_file_approval_status') === 'Rejected' &&
                                isPending3DApproval(getLineItem?.production_3d_file_approval_status) ? (
                                <>
                                    <Form.Item
                                        label="Document Rejection Feedback"
                                        {...formItemLayout}
                                        name="production_3d_file_approval_feedback"
                                        rules={[{ required: true, message: 'Please input a feedback' }]}
                                        initialValue={getLineItem?.production_3d_file_approval_feedback}
                                        className="w-full"
                                        help="Your Feedback will be sent to your Sales Representative"
                                    >
                                        <Input.TextArea
                                            placeholder="Your Feedback"
                                            minLength={5}
                                            maxLength={200}
                                        ></Input.TextArea>
                                    </Form.Item>
                                </>
                            ) : (
                                getLineItem?.production_3d_file_approval_status === 'Rejected' && (
                                    <span className="font-bold">
                                        Your 3D Document is being reviewed by the Sales Representative, we will send you
                                        status updates via email
                                    </span>
                                )
                            );
                        }}
                    </Form.Item>
                </div>
            </Form>
        </Modal>
    );
};

export default observer(ThreeDApproval);
